import { ChangeEvent, RefObject, useEffect, useState } from 'react'
import { Text, Modal, LoadingAnimation, Icon } from '@/components'
import { AlertMessage } from '@/app/components'
import { AnimatePresence, motion } from 'motion/react'
import { match } from 'ts-pattern'

/** How long each rotating loading message is shown before advancing, in ms. */
const LOADING_MESSAGE_INTERVAL_MS = 4000

export type BillUploadStatus = 'idle' | 'loading' | 'success' | 'error'

export type BillUploadCardProps = {
  fileInputRef: RefObject<HTMLInputElement>
  handleFilesUploaded: (event: ChangeEvent<HTMLInputElement>) => void
  handleUploadClick: () => void
  fileUploadStatus: BillUploadStatus
  fileUploadName: string | null
  uploadErrorMessage: { title: string; subtitle: string } | null
  uploadSuccessMessage: string | null
  /** When set, the uploaded file name links to the bill (opens in a new tab). */
  billUrl?: string | null
  utilityBillExampleSrc?: string | null
  /**
   * Optional rotating status messages shown under the loading indicator while a
   * bill is processing (e.g. "Finding your utility", "Creating your plan"). When
   * omitted the card shows the default "Processing utility bill" text.
   */
  loadingMessages?: string[]
  /**
   * Hides the "Option 1: Upload document" heading. Use when the card isn't
   * presented as one of two numbered options (e.g. the utility selection page).
   */
  hideTitle?: boolean
  /**
   * Suppresses the built-in "<file> uploaded successfully" banner. Use when the
   * consumer shows its own consolidated success message (e.g. the utility
   * selection page combines it with the matched-utility/plan result).
   */
  hideSuccessBanner?: boolean
}

function UtilityBillExampleModal(props: {
  src: string
  open: boolean
  onClose: () => void
}) {
  return (
    <Modal
      id="utility-bill-example-modal"
      title="Utility bill example"
      open={props.open}
      onClose={props.onClose}
    >
      <div className="mt-14 max-h-96">
        <img src={props.src} alt="Utility bill example" className="w-full" />
      </div>
    </Modal>
  )
}

/**
 * Rotates through the provided loading messages, holding each for
 * `LOADING_MESSAGE_INTERVAL_MS` before advancing and stopping on the last one
 * (it does not loop back to the start). Kept as its own component so its
 * per-message re-renders stay isolated and don't restart the sibling
 * `LoadingAnimation`.
 */
function BillUploadLoadingText({ messages }: { messages: string[] }) {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    // Hold on the final message rather than cycling back to the beginning.
    if (index >= messages.length - 1) {
      return
    }
    const timeout = setTimeout(
      () => setIndex((current) => Math.min(current + 1, messages.length - 1)),
      LOADING_MESSAGE_INTERVAL_MS,
    )
    return () => clearTimeout(timeout)
  }, [index, messages.length])

  const text = messages[index] ?? 'Processing utility bill'

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={text}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.3 }}
        className="inline-block"
      >
        {text}
      </motion.span>
    </AnimatePresence>
  )
}

/**
 * "Option 1: Upload document" card — the bill upload drop zone plus the
 * success / error / info alerts. Shared by the enrollment account step and the
 * profile utility-account page so both look and behave identically.
 */
export default function BillUploadCard({
  fileInputRef,
  handleFilesUploaded,
  handleUploadClick,
  fileUploadStatus,
  fileUploadName,
  uploadErrorMessage,
  uploadSuccessMessage,
  billUrl,
  utilityBillExampleSrc,
  loadingMessages,
  hideTitle = false,
  hideSuccessBanner = false,
}: BillUploadCardProps) {
  const [exampleModalOpen, setExampleModalOpen] = useState(false)

  const hasLoadingMessages = (loadingMessages?.length ?? 0) > 0

  const borderColor = match(fileUploadStatus)
    .with('error', () => 'border-red-500')
    .with('success', () => 'border-green-500')
    .otherwise(() => 'border-gray-300')

  return (
    <div>
      {!hideTitle && (
        <Text variant="body1" className="font-bold mb-4">
          Option 1: Upload document
        </Text>
      )}
      <div
        className={`border-[1px] border-solid ${borderColor} rounded-lg p-8 flex flex-col items-center justify-center gap-4 cursor-pointer`}
        onClick={handleUploadClick}
      >
        <div
          className={`flex flex-col items-center rounded-lg p-2 ${
            fileUploadStatus === 'loading' ? '' : 'bg-themed-grey-100'
          }`}
        >
          {fileUploadStatus === 'loading' ? (
            <LoadingAnimation type="falling" size="small" />
          ) : (
            <Icon name="Upload" size={24} color="grey-500" />
          )}
        </div>
        <div className="flex flex-col items-center gap-1">
          <Text variant="subheader">
            {match({ status: fileUploadStatus, hasLoadingMessages })
              .with({ status: 'loading', hasLoadingMessages: true }, () => (
                <BillUploadLoadingText messages={loadingMessages as string[]} />
              ))
              .with({ status: 'loading' }, () => 'Processing utility bill')
              .otherwise(() => 'Upload utility bill')}
          </Text>
          <Text variant="body2" className="text-themed-grey-500">
            Supports PDF, JPG, PNG (Max 10MB)
          </Text>
        </div>
      </div>
      <input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        ref={fileInputRef}
        onChange={handleFilesUploaded}
        className="hidden"
      />

      <AnimatePresence>
        {fileUploadStatus === 'success' && !hideSuccessBanner && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-4"
          >
            <AlertMessage variant="success">
              {fileUploadName && (
                <Text className="text-initial">
                  {billUrl ? (
                    <a
                      href={billUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="!underline cursor-pointer"
                    >
                      Your bill
                    </a>
                  ) : (
                    'Your bill'
                  )}{' '}
                  uploaded successfully.
                </Text>
              )}
              {uploadSuccessMessage && (
                <Text variant="body2" className="mt-2 text-initial">
                  {uploadSuccessMessage}
                </Text>
              )}
            </AlertMessage>
          </motion.div>
        )}
        {fileUploadStatus === 'error' && uploadErrorMessage && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-4"
          >
            <AlertMessage variant="error">
              <div className="flex flex-col gap-1">
                <Text className="body3 text-initial text-red-500 font-bold">
                  {uploadErrorMessage.title}
                </Text>
                <Text className="body2 text-initial text-red-500 !font-medium">
                  {uploadErrorMessage.subtitle}
                </Text>
              </div>
            </AlertMessage>
          </motion.div>
        )}
      </AnimatePresence>

      {fileUploadStatus !== 'success' && (
        <div className="mt-4">
          <AlertMessage variant="info">
            <div className="flex flex-col gap-1">
              Make sure your account details are clearly visible in the
              document.
              {utilityBillExampleSrc && (
                <Text
                  variant="link"
                  className="text-themed-blue-500 cursor-pointer mt-1"
                  onClick={() => setExampleModalOpen(true)}
                >
                  View bill example
                </Text>
              )}
            </div>
          </AlertMessage>
        </div>
      )}

      {utilityBillExampleSrc && (
        <UtilityBillExampleModal
          src={utilityBillExampleSrc}
          open={exampleModalOpen}
          onClose={() => setExampleModalOpen(false)}
        />
      )}
    </div>
  )
}
